import { ref, computed, type Ref } from 'vue';
import { useStorage } from '@vueuse/core';
import {
  deleteConversation as apiDelete,
  fetchCurrentGeneralConversation,
  getConversation,
  listConversations,
  renameConversation as apiRename,
  type ConversationSummary,
  type PersistedMessage,
  type Proposal,
} from '@/api/copilot';

export interface UICopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
  toolRunning?: string;
  proposals?: ProposalState[];
}

export interface ProposalState {
  proposal: Proposal;
  status: 'pending' | 'applying' | 'applied' | 'rejected' | 'failed' | 'silent_applied' | 'reverted';
  error?: string;
  /** Per ADR-0043. Set on `silent_applied` state. Calling this reverts the
   *  applied change (theme → prior theme, dev mode → prior enabled flag,
   *  etc.). `null` when the proposal has no available undo path. */
  undo?: (() => Promise<void> | void) | null;
}

const LOCAL_KEY = 'avp.copilot.currentConversationId.v1';

const currentId = useStorage<string | null>(LOCAL_KEY, null);
const messages: Ref<UICopilotMessage[]> = ref([]);
const conversations: Ref<ConversationSummary[]> = ref([]);

/**
 * One-shot seed message. When set (e.g. from a "Спросить помощника" button
 * inside an editor), the Sheet picks it up on next open and pre-fills the
 * input — does NOT auto-send, the user still chooses to press Enter.
 *
 * This is a one-time hand-off, cleared after the Sheet consumes it.
 */
const seed: Ref<{ message: string; startNew: boolean } | null> = ref(null);

/**
 * Pinia-style global handle for the active Copilot conversation. Lives at
 * module scope so the chat survives the Sheet being closed and reopened.
 */
export function useCopilotConversation() {
  const isFresh = computed(() => currentId.value === null);

  async function refreshList(): Promise<void> {
    try {
      conversations.value = await listConversations({ limit: 50 });
    } catch {
      // Non-fatal — list just stays as-is.
    }
  }

  async function loadConversation(id: string): Promise<void> {
    const detail = await getConversation(id);
    currentId.value = detail.id;
    messages.value = detail.messages.map(messageFromPersisted);
  }

  function startNew(): void {
    currentId.value = null;
    messages.value = [];
  }

  /**
   * Per ADR-0044 / philosophy §1.5 ("one assistant identity"). Asks the
   * server for the user's single general-conversation id and sets it as
   * current. The server creates the thread on first call, so this is
   * idempotent. The SPA prefers this over the localStorage-cached id
   * when the cached one fails to load (e.g. user signed in on a fresh
   * device, or the cached id refers to a deleted conversation). Best-
   * effort: any failure leaves the composable in its current state for
   * the caller to handle.
   */
  async function resolveGeneralFromServer(): Promise<void> {
    const summary = await fetchCurrentGeneralConversation();
    await loadConversation(summary.id);
  }

  async function rename(id: string, title: string): Promise<void> {
    await apiRename(id, title);
    await refreshList();
  }

  async function remove(id: string): Promise<void> {
    await apiDelete(id);
    if (currentId.value === id) startNew();
    await refreshList();
  }

  function setSeed(message: string, opts: { startNew?: boolean } = {}): void {
    seed.value = { message, startNew: opts.startNew ?? true };
  }

  function consumeSeed(): { message: string; startNew: boolean } | null {
    const value = seed.value;
    seed.value = null;
    return value;
  }

  return {
    currentId,
    isFresh,
    messages,
    conversations,
    seed,
    setSeed,
    consumeSeed,
    refreshList,
    loadConversation,
    resolveGeneralFromServer,
    startNew,
    rename,
    remove,
  };
}

function messageFromPersisted(p: PersistedMessage): UICopilotMessage {
  const proposals: ProposalState[] | undefined = p.proposals
    ? p.proposals.map((proposal) => ({ proposal, status: 'pending' as const }))
    : undefined;
  const out: UICopilotMessage = {
    id: p.id,
    role: p.role,
    content: p.content,
  };
  if (proposals && proposals.length > 0) out.proposals = proposals;
  return out;
}
